import React, { useCallback, useEffect, useState, useMemo } from "react";
import {
  Box,
  Card,
  CardMedia,
  Button,
  Typography,
  Chip,
  Stack,
  Grid,
  Divider,
  Paper,
  Link,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  OpenInNew as OpenInNewIcon,
  Language as LanguageIcon,
  CalendarToday as CalendarIcon,
  Dns as DnsIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  InsertDriveFile as InsertDriveFileIcon,
  Autorenew as AutorenewIcon,
  Close as CloseIcon,
} from "@mui/icons-material";

import { useParams, useNavigate } from "react-router-dom";
import { confirmAlert } from "react-confirm-alert";
import { styled } from "@mui/material/styles";

import {
  CloudFunctions,
  Firestore,
} from "../../components/FirebaseAuth/firebase";
import { defaultTemplateUrl } from "../../utils/index";
import {
  LOCAL_STORAGE_CURRENT_ORG,
  getLocalStorage,
} from "../../utils/localStorage";
import { webCheckUrl } from "../../utils/constants";
import {
  collection,
  doc,
  getDocs,
  query,
  where,
  setDoc,
  deleteDoc,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";

// Styled components for better reusability
const StatusChip = styled(Chip)(({ theme, status }) => {
  const getStatusColor = () => {
    switch (status) {
      case "active":
      case "production":
        return {
          bgcolor: theme.palette.success.light,
          color: theme.palette.success.dark,
        };
      case "pending":
      case "pending-test":
      case "pending-production":
      case "test":
        return {
          bgcolor: theme.palette.warning.light,
          color: theme.palette.warning.dark,
        };
      default:
        return {
          bgcolor: theme.palette.info.light,
          color: theme.palette.info.dark,
        };
    }
  };

  return {
    ...getStatusColor(),
    fontWeight: "medium",
    textTransform: "capitalize",
    "& .MuiChip-label": {
      px: 1,
    },
  };
});

// Styled components
const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.08)",
  overflow: "visible",
  position: "relative",
  transition: "transform 0.3s ease, box-shadow 0.3s ease",
  "&:hover": {
    boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.12)",
    transform: "translateY(-2px)",
  },
}));

const SiteImage = styled(CardMedia)(({ theme }) => ({
  height: 200,
  borderRadius: theme.shape.borderRadius,
  objectFit: "cover",
  maxWidth: "100%",
  margin: {
    xs: 0,
    sm: 0,
    md: theme.spacing(2, 0, 2, 2),
  },
}));

const SiteDetailsCard = ({ siteData, setIsDeleting }) => {
  const {
    siteName,
    siteType,
    description,
    image,
    webCheckResult,
    slug,
    status,
    createdAt,
    lastUpdated,
  } = siteData;

  const { accountId, siteId } = useParams();
  const history = useNavigate();

  const [siteImage, setSiteImage] = useState("");
  const [healthStatus, setHealthStatus] = useState({
    status: "healthy", // healthy, warning, error
    message: "All systems operational",
  });
  const [isRequestingDeepScan, setIsRequestingDeepScan] = useState(false);
  const [hasRequestedDeepScan, setHasRequestedDeepScan] = useState(false);

  const handleEdit = () => {
    const url = `/account/${accountId}/sites/edit/${siteId}`;
    history(url);
  };

  const handleClickLink = useCallback(() => {
    window.open(siteData.url, "_blank");
  }, [siteData]);

  async function deleteSiteUrls() {
    try {
      const deploySitesRef = collection(Firestore, "deploySites");
      const deploySitesQuery = query(
        deploySitesRef,
        where("siteId", "==", siteId)
      );
      const deploySitesSnapshot = await getDocs(deploySitesQuery);
      if (!deploySitesSnapshot.empty) {
        for (let deploySite of deploySitesSnapshot.docs) {
          await setDoc(deploySite.ref, { status: "deleted" }, { merge: true });
        }
      }
      return;
    } catch (ex) {
      console.error("Error deleting site URLs:", ex);
    }
  }

  async function deleteFromOrganization() {
    try {
      const currentOrganization = getLocalStorage(LOCAL_STORAGE_CURRENT_ORG);
      const refOrganization = doc(
        Firestore,
        "organizations",
        currentOrganization.id
      );
      const updatedTemplates = currentOrganization?.templates?.length
        ? currentOrganization.templates.filter((temp) => temp.siteId !== siteId)
        : [];

      await setDoc(refOrganization, {
        ...currentOrganization,
        templates: updatedTemplates,
      });
    } catch (ex) {
      console.error("Error deleting from organization:", ex);
    }
  }

  const handleDeleted = async (site) => {
    try {
      setIsDeleting(true);
      await deleteFromOrganization();
      // Mark site as deleted in the account's sites collection
      const siteRef = doc(Firestore, `/accounts/${accountId}/sites`, siteId);
      await setDoc(siteRef, { status: "deleted" }, { merge: true });

      // Delete invoices related to the site
      const invoicesRef = collection(
        Firestore,
        `/accounts/${accountId}/sites/${siteId}/invoices`
      );
      const invoicesQuery = query(invoicesRef, where("siteId", "==", siteId));
      const invoiceSnapshot = await getDocs(invoicesQuery);

      if (!invoiceSnapshot.empty) {
        for (let invoice of invoiceSnapshot.docs) {
          await deleteDoc(invoice.ref);
        }
      }
      // Call Cloud Function to update the site tag
      const updateSiteTag = httpsCallable(
        CloudFunctions,
        "kwallCloud-updateSiteTag"
      );
      await updateSiteTag({
        command: "tag:add",
        tag: "DELETED",
        slug,
      });
      // Delete associated site URLs
      await deleteSiteUrls();
      setIsDeleting(false);
      history(`/account/${accountId}/sites`);
    } catch (ex) {
      setIsDeleting(false);
      console.error("Error deleting site:", ex);
    }
  };

  const handleDelete = async (site) => {
    confirmAlert({
      title: "Delete Site",
      message:
        "Are you sure you want to delete this site? This action cannot be undone.",
      buttons: [
        {
          label: "Delete",
          onClick: async () => handleDeleted(site),
          className: "react-confirm-alert-button-delete",
        },
        {
          label: "Cancel",
          onClick: () => {},
          className: "react-confirm-alert-button-cancel",
        },
      ],
      overlayClassName: "react-confirm-alert-overlay-custom",
    });
  };

  const handleClickWebCheck = useCallback(() => {
    const url = siteData.url.split("://")?.[1];
    window.open(`${webCheckUrl}/${url}`, "_blank");
  }, [siteData.url]);

  // Deep scan logic - adapted from ExternalSiteCard
  const disableScan = useMemo(() => {
    if (isRequestingDeepScan) {
      return true;
    } else if (hasRequestedDeepScan) {
      return true;
    } else {
      const deepScanTimeSeconds = siteData?.["deep_scan_time_external"]?.seconds || 0;
      const deepScanTimeDate = new Date(deepScanTimeSeconds * 1000);
    
      // Get today's date without the time
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      // Check if the date is less than today
      return deepScanTimeDate >= today;
    }
  }, [siteData, isRequestingDeepScan, hasRequestedDeepScan]);

  const handleRequestScan = useCallback(async () => {
    try {
      setIsRequestingDeepScan(true);

      const requestDeepScan = httpsCallable(
        CloudFunctions,
        "kwallCloud-requestDeepScan"
      );
      await requestDeepScan({
        siteId: siteId,
        domain: siteData.url,
        env: "external",
        site_name_slug: siteData.slug,
        accountId: accountId,
      });
      setHasRequestedDeepScan(true);
    } catch (_error) {
      setHasRequestedDeepScan(false);
      console.log("Error requesting deep scan:", _error);
    } finally {
      setIsRequestingDeepScan(false);
    }
  }, [accountId, siteId, siteData]);

  const handleClickDeepScan = useCallback(async () => {
    confirmAlert({
      customUI: ({ onClose }) => {
        return (
          <Card
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "24px",
              padding: "24px",
              maxWidth: "360px",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <Typography fontSize={24} fontWeight={700}>
                Alert
              </Typography>
              <CloseIcon
                onClick={onClose}
                sx={{ cursor: "pointer", color: "#666", marginTop: "-12px" }}
              />
            </Box>
            <Typography fontSize={20} color="#666">
              This scan will take several hours to complete
            </Typography>
            <Box sx={{ display: "flex", gap: "8px" }}>
              <Button
                onClick={() => {
                  handleRequestScan();
                  onClose();
                }}
                variant="contained"
              >
                Run Scan
              </Button>
              <Button onClick={onClose} variant="outlined" color="error">
                Cancel
              </Button>
            </Box>
          </Card>
        );
      },
    });
  }, [handleRequestScan]);

  const handleClickViewScan = useCallback(() => {
    const path = `https://reporting.accorbis.com${
      siteData?.[`deep_scan_result_external`]
    }/index.html`;
    window.open(path, "_blank");
  }, [siteData]);

  useEffect(() => {
    if (image) {
      setSiteImage(image);
    } else {
      const thumbnails =
        webCheckResult?.audits?.["screenshot-thumbnails"]?.details?.items;
      if (thumbnails?.length) {
        setSiteImage(thumbnails[thumbnails.length - 1]?.data);
      } else {
        setSiteImage(defaultTemplateUrl);
      }
    }

    if (webCheckResult?.audits) {
      const performanceScore =
        webCheckResult?.audits?.["performance"]?.score || 0;
      if (performanceScore < 0.5) {
        setHealthStatus({
          status: "error",
          message: "Performance issues detected",
        });
      } else if (performanceScore < 0.8) {
        setHealthStatus({
          status: "warning",
          message: "Minor performance concerns",
        });
      }
    }
  }, [image, webCheckResult?.audits]);

  // Format date
  const formatDate = (dateObj) => {
    if (!dateObj) return "N/A";

    const date =
      dateObj instanceof Date
        ? dateObj
        : dateObj.toDate
        ? dateObj.toDate()
        : new Date(dateObj);
    return new Intl.DateTimeFormat("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    }).format(date);
  };

  // Get status color
  const getStatusColor = (status) => {
    if (!status) return "default";

    const statusMap = {
      active: "success",
      pending: "warning",
      deleted: "error",
      draft: "info",
    };

    // Handle status with prefixes like 'pending-test'
    for (const [key, value] of Object.entries(statusMap)) {
      if (status.toLowerCase().includes(key)) {
        return value;
      }
    }

    return "default";
  };

  // Get health status icon
  const getHealthStatusIcon = () => {
    switch (healthStatus.status) {
      case "healthy":
        return <CheckCircleIcon fontSize="small" color="success" />;
      case "warning":
        return <WarningIcon fontSize="small" color="warning" />;
      case "error":
        return <WarningIcon fontSize="small" color="error" />;
      default:
        return null;
    }
  };

  const formatStatus = (status) => {
    return status?.replace(/-/g, " ");
  };

  return (
    <StyledCard>
      <Grid container>
        <Grid
          item
          xs={12}
          md={4}
          lg={3}
          sx={{ maxWidth: "100%", overflow: "hidden" }}
        >
          <SiteImage component="img" image={siteImage} alt={siteName} />
        </Grid>

        <Grid item xs={12} md={8} lg={9}>
          <Box sx={{ p: 3 }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "column", md: "row" },
                justifyContent: {
                  xs: "flex-start",
                  sm: "flex-start",
                  md: "space-between",
                },
                alignItems: { xs: "stretch", sm: "stretch", md: "flex-start" },
                gap: { xs: 2, sm: 2, md: 0 },
                mb: 2,
              }}
            >
              <Box>
                <Typography variant="h4" gutterBottom>
                  {siteName}
                </Typography>

                <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                  <Chip
                    label={siteType}
                    size="small"
                    icon={<DnsIcon />}
                    color="primary"
                    variant="outlined"
                  />
                  {status && (
                    <StatusChip
                      label={formatStatus(status)}
                      status={status}
                      size="small"
                    />
                  )}
                  {siteType === "Manual" && (
                    <Chip
                      label="External"
                      size="small"
                      sx={{
                        bgcolor: "grey.200",
                        color: "grey.700",
                        fontWeight: "medium",
                      }}
                    />
                  )}
                  <Tooltip title={healthStatus.message}>
                    <Chip
                      label="Health"
                      size="small"
                      icon={getHealthStatusIcon()}
                      color={
                        healthStatus.status === "healthy"
                          ? "success"
                          : healthStatus.status === "warning"
                          ? "warning"
                          : "error"
                      }
                      variant="outlined"
                    />
                  </Tooltip>
                </Stack>
              </Box>

              <Stack direction="row" spacing={1}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<EditIcon />}
                  onClick={handleEdit}
                  sx={{ borderRadius: "8px" }}
                >
                  <Typography>Edit</Typography>
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  startIcon={<DeleteIcon />}
                  onClick={() => handleDelete(siteData)}
                  sx={{ borderRadius: "8px" }}
                >
                  <Typography>Delete</Typography>
                </Button>
              </Stack>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Grid container spacing={3}>
              <Grid item xs={12} md={8}>
                <Typography variant="body1" paragraph>
                  {description}
                </Typography>
              </Grid>

              <Grid item xs={12} md={4}>
                <Paper
                  elevation={0}
                  sx={{ p: 2, bgcolor: "background.default", borderRadius: 2 }}
                >
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    gutterBottom
                  >
                    Site Details
                  </Typography>

                  <Stack spacing={1.5}>
                    {siteData.url && (
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <LanguageIcon
                          fontSize="small"
                          sx={{ mr: 1, color: "text.secondary" }}
                        />
                        <Typography variant="body2" sx={{ mr: 1 }}>
                          URL:
                        </Typography>
                        <Link
                          href={siteData.url}
                          target="_blank"
                          rel="noopener"
                          onClick={handleClickLink}
                          sx={{ display: "flex", alignItems: "center" }}
                        >
                          <Typography
                            variant="body2"
                            noWrap
                            sx={{ maxWidth: 150 }}
                          >
                            {siteData.url.replace(/^https?:\/\//, "")}
                          </Typography>
                          <OpenInNewIcon
                            fontSize="small"
                            sx={{ ml: 0.5, fontSize: "0.8rem" }}
                          />
                        </Link>
                      </Box>
                    )}

                    {createdAt && (
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <CalendarIcon
                          fontSize="small"
                          sx={{ mr: 1, color: "text.secondary" }}
                        />
                        <Typography variant="body2" sx={{ mr: 1 }}>
                          Created:
                        </Typography>
                        <Typography variant="body2">
                          {formatDate(createdAt)}
                        </Typography>
                      </Box>
                    )}

                    {lastUpdated && (
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <CalendarIcon
                          fontSize="small"
                          sx={{ mr: 1, color: "text.secondary" }}
                        />
                        <Typography variant="body2" sx={{ mr: 1 }}>
                          Last Updated:
                        </Typography>
                        <Typography variant="body2">
                          {formatDate(lastUpdated)}
                        </Typography>
                      </Box>
                    )}

                    {siteType === "Manual" && (
                      <Box sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 1 }}>
                        <Button
                          variant="outlined"
                          size="small"
                          fullWidth
                          onClick={handleClickWebCheck}
                          startIcon={<LanguageIcon />}
                          sx={{ borderRadius: "8px" }}
                        >
                          Web Check Report
                        </Button>
                        
                        <Box sx={{ display: "flex", gap: 1 }}>
                          <Button
                            variant="contained"
                            size="small"
                            onClick={handleClickViewScan}
                            disabled={!siteData?.[`deep_scan_result_external`]}
                            startIcon={<InsertDriveFileIcon />}
                            sx={{ borderRadius: "8px", flex: 1 }}
                          >
                            View Last Scan
                          </Button>
                          <Button
                            variant="contained"
                            size="small"
                            onClick={handleClickDeepScan}
                            disabled={disableScan}
                            startIcon={isRequestingDeepScan ? 
                              <CircularProgress size={16} color="inherit" /> : 
                              <AutorenewIcon />
                            }
                            sx={{ borderRadius: "8px", flex: 1 }}
                          >
                            {isRequestingDeepScan ? "Scanning..." : "Deep Scan"}
                          </Button>
                        </Box>
                      </Box>
                    )}
                  </Stack>
                </Paper>
              </Grid>
            </Grid>
          </Box>
        </Grid>
      </Grid>
    </StyledCard>
  );
};

export default SiteDetailsCard;
